import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Trash2, Car, Plus, CalendarPlus, ChevronRight, Wrench, FileText, Banknote, User, History, WalletCards, Phone } from "lucide-react";
import { VehicleForm } from "@/pages/Vehicles";
import { ColorChip } from "@/components/ColorChip";
import { colorSwatchHex } from "@/lib/vehicleCatalog";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

function CustomerSectionHeading({ number, icon: Icon, title }) {
  return (
    <div className="stellar-customer-section-heading">
      <span className="stellar-customer-section-step">{number}</span>
      <Icon className="h-4 w-4" />
      <h2 className="font-display text-base font-bold">{title}</h2>
    </div>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({
    customer_id: id, manufacturer: "", model: "", year: "", license_plate: "", vin: "",
    color: "", mileage: "", fuel_type: "", body_type: "", notes: "", engine: "", internal_reference: "", registration_date: "",
  });
  const { data: customer } = useQuery({
    queryKey: ["customer", id],
    queryFn: async () => (await api.get(`/customers/${id}`)).data,
  });
  const { data: appts = [] } = useQuery({
    queryKey: ["customer-appts", id],
    queryFn: async () => (await api.get(`/customers/${id}/appointments`)).data,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["customer-vehicles", id],
    queryFn: async () => (await api.get(`/customers/${id}/vehicles`)).data,
  });
  const { data: history } = useQuery({
    queryKey: ["customer-history", id],
    queryFn: async () => (await api.get(`/customers/${id}/history`)).data,
  });
  const [form, setForm] = useState(null);
  const [carsStr, setCarsStr] = useState("");
  const [platesStr, setPlatesStr] = useState("");

  const createVehicleMut = useMutation({
    mutationFn: async () => (await api.post("/vehicles", {
      ...vehicleForm,
      customer_id: id,
      year: vehicleForm.year ? Number(vehicleForm.year) : null,
      mileage: vehicleForm.mileage ? Number(vehicleForm.mileage) : null,
      registration_date: vehicleForm.registration_date || null,
    })).data,
    onSuccess: (v) => {
      toast.success("Fahrzeug angelegt");
      qc.invalidateQueries({ queryKey: ["customer-vehicles", id] });
      setVehicleOpen(false);
      setVehicleForm({ customer_id: id, manufacturer: "", model: "", year: "", license_plate: "", vin: "", color: "", mileage: "", fuel_type: "", body_type: "", notes: "", engine: "", internal_reference: "", registration_date: "" });
      navigate(`/fahrzeuge/${v.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  useEffect(() => {
    if (customer) {
      setForm(customer);
      setCarsStr((customer.cars || []).join(", "));
      setPlatesStr((customer.plates || []).join(", "));
    }
  }, [customer]);

  const updateMut = useMutation({
    mutationFn: async () => (await api.put(`/customers/${id}`, {
      ...form,
      cars: carsStr.split(",").map(s => s.trim()).filter(Boolean),
      plates: platesStr.split(",").map(s => s.trim().toUpperCase()).filter(Boolean),
    })).data,
    onSuccess: () => { toast.success("Gespeichert"); qc.invalidateQueries({ queryKey: ["customer", id] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/customers/${id}`)).data,
    onSuccess: () => { toast.success("Kunde gelöscht"); window.location.href = "/kunden"; },
  });

  if (!form) return <div className="p-8 text-muted-foreground">Laden…</div>;

  const allPhotos = appts.flatMap((a) => (a.photos || []).map((p) => ({ ...p, appt: a })));
  const paidTotal = (history?.payments || []).reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const lastAppointment = [...appts]
    .filter((appointment) => appointment.start)
    .sort((a, b) => new Date(b.start) - new Date(a.start))[0];

  const openNewAppointment = () => {
    const onlyVehicle = vehicles.length === 1 ? vehicles[0] : null;
    const params = new URLSearchParams({
      new: "1",
      customer_id: id,
      name: customer.name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      address: customer.address || "",
    });
    if (onlyVehicle) {
      params.set("vehicle_id", onlyVehicle.id);
      params.set("vehicle_brand", onlyVehicle.manufacturer || "");
      params.set("vehicle_model", onlyVehicle.model || "");
      params.set("vehicle_year", onlyVehicle.year || "");
      params.set("vehicle_color", onlyVehicle.color || "");
      params.set("plate", onlyVehicle.license_plate || "");
    }
    navigate(`/kalender?${params.toString()}`);
  };

  return (
    <div className="stellar-page stellar-detail-page">
      <Link to="/kunden" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-customers">
        <ArrowLeft className="h-4 w-4" /> Kunden
      </Link>
      <div className="stellar-detail-heading mb-6">
        <div className="stellar-icon-bubble stellar-icon-green"><User /></div>
        <div>
          <div className="stellar-eyebrow">Kunde</div>
          <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight break-words">{customer.name}</h1>
        </div>
      </div>

      <section className="stellar-customer-section">
        <CustomerSectionHeading number="1" icon={User} title="Kundenübersicht" />
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="premium-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <div className="stellar-section-icon stellar-section-green"><User /></div>
              <h2 className="font-display font-semibold">Kontaktdaten</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="edit-name" /></div>
              <div>
                <Label>Telefon</Label>
                <div className="flex gap-2">
                  <Input className="min-w-0" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="edit-phone" />
                  {form.phone && (
                    <Button asChild type="button" variant="outline" className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-50" title={`${form.phone} anrufen`}>
                      <a href={`tel:${String(form.phone).replace(/[^\d+]/g, "")}`} data-testid="call-customer">
                        <Phone className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Anrufen</span>
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              <div><Label>E-Mail</Label><Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Adresse (optional)</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
              <div className="sm:col-span-2"><Label>Notizen (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
            </div>
          </div>

          <div className="premium-card p-5" data-testid="customer-quick-overview">
            <div className="mb-4 flex items-center gap-2">
              <div className="stellar-section-icon stellar-section-green"><WalletCards /></div>
              <h2 className="font-display font-semibold">Auf einen Blick</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-violet-50/70 p-3">
                <div className="text-xs text-muted-foreground">Fahrzeuge</div>
                <div className="mt-1 font-display text-xl font-semibold text-violet-700">{vehicles.length}</div>
              </div>
              <div className="rounded-xl bg-blue-50/70 p-3">
                <div className="text-xs text-muted-foreground">Termine</div>
                <div className="mt-1 font-display text-xl font-semibold text-blue-700">{appts.length}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Aufträge</div>
                <div className="mt-1 font-display text-xl font-semibold">{history?.jobs?.length || 0}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Rechnungen</div>
                <div className="mt-1 font-display text-xl font-semibold">{history?.invoices?.length || 0}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Letzter Termin</div>
                <div className="mt-1 font-display text-base font-semibold">{lastAppointment ? new Date(lastAppointment.start).toLocaleDateString("de-DE") : "—"}</div>
              </div>
              <div className="rounded-xl bg-emerald-50/70 p-3">
                <div className="text-xs text-muted-foreground">Bezahlt</div>
                <div className="mt-1 font-display text-base font-semibold text-emerald-700">{fmtEur(paidTotal)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="stellar-customer-section">
        <CustomerSectionHeading number="2" icon={Car} title="Fahrzeuge" />
          <div className="premium-card overflow-hidden">
            <div className="p-4 border-b border-border/60 flex flex-wrap items-center gap-2">
              <div className="stellar-section-icon stellar-section-violet"><Car /></div>
              <div className="font-display font-semibold">Fahrzeuge ({vehicles.length})</div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <Dialog open={vehicleOpen} onOpenChange={setVehicleOpen}>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setVehicleOpen(true)} data-testid="add-vehicle-from-customer">
                  <Plus className="h-4 w-4 mr-1" /> Fahrzeug hinzufügen
                </Button>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Neues Fahrzeug für {customer.name}</DialogTitle></DialogHeader>
                  <VehicleForm form={vehicleForm} setForm={setVehicleForm} customers={[]} disableCustomer />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setVehicleOpen(false)}>Abbrechen</Button>
                    <Button onClick={() => createVehicleMut.mutate()} disabled={!vehicleForm.manufacturer || !vehicleForm.model} data-testid="save-vehicle-from-customer">
                      Speichern
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            <div className="divide-y divide-border/60">
              {vehicles.length === 0 && <div className="p-6 text-sm text-muted-foreground">Noch keine Fahrzeuge.</div>}
              {vehicles.map((v) => (
                <Link key={v.id} to={`/fahrzeuge/${v.id}`} data-testid={`customer-vehicle-${v.id}`} className="flex items-center gap-3 p-4 hover:bg-muted/40">
                  <div
                    className="stellar-icon-bubble !h-10 !w-10 !basis-10 border"
                    style={{
                      color: colorSwatchHex(v.color) || "#64748b",
                      background: `${colorSwatchHex(v.color) || "#94a3b8"}20`,
                      borderColor: `${colorSwatchHex(v.color) || "#94a3b8"}55`,
                    }}
                  >
                    <Car className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{v.manufacturer} {v.model}{v.year && <span className="font-normal text-muted-foreground"> · Bj. {v.year}</span>}</div>
                    <div className="text-xs text-muted-foreground">
                      {v.license_plate || "Kein Kennzeichen"}
                      {v.color && (
                        <>
                          {" "}·{" "}
                          <ColorChip value={v.color} testId={`customer-vehicle-color-${v.id}`} />
                        </>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
      </section>

      <section className="stellar-customer-section">
          <CustomerSectionHeading number="3" icon={CalendarPlus} title="Termine & Aufträge" />
          <div className="premium-card overflow-hidden">
            <div className="p-4 border-b border-border/60 font-display font-semibold flex items-center gap-2"><div className="stellar-section-icon stellar-section-blue"><History /></div>Verlauf ({appts.length} Termine)</div>
            <div className="divide-y divide-border/60">
              {appts.length === 0 && <div className="p-6 text-sm text-muted-foreground">Keine Termine.</div>}
              {appts.map((a) => (
                <Link
                  key={a.id}
                  to={a.job_id ? `/auftraege/${a.job_id}` : `/kalender?open=${a.id}`}
                  className="flex items-center justify-between gap-4 p-4 hover:bg-muted/40"
                  data-testid={`history-appt-${a.id}`}
                >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium">{a.service_name}</div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <span>{new Date(a.start).toLocaleString("de-DE")} · {[a.car_model, a.vehicle_year ? `Bj. ${a.vehicle_year}` : "", a.plate].filter(Boolean).join(" · ")}</span>
                        {a.vehicle_color && (
                          <>
                            <span>·</span>
                            <ColorChip value={a.vehicle_color} testId={`history-vehicle-color-${a.id}`} />
                          </>
                        )}
                      </div>
                      {a.notes && <div className="text-xs text-muted-foreground mt-1 italic">&ldquo;{a.notes}&rdquo;</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-medium">{fmtEur(a.final_price || a.estimated_price)}</div>
                      {(() => {
                        // Sprint 18 — appointment history now shows the appointment/conversion status,
                        // NOT the misleading legacy payment status (which belonged to the invoice).
                        if (a.job_id) {
                          return (
                            <div className="inline-flex items-center gap-1 text-xs mt-0.5 text-primary" data-testid="appt-status-converted-job">
                              In Auftrag umgewandelt
                              <ChevronRight className="h-3 w-3" />
                            </div>
                          );
                        }
                        if (a.converted_to_appointment_id) {
                          return (
                            <div className="text-xs mt-0.5 text-emerald-600 dark:text-emerald-400" data-testid="appt-status-converted-arbeitstermin">
                              In Arbeitstermin umgewandelt
                            </div>
                          );
                        }
                        const now = Date.now();
                        const end = a.end ? new Date(a.end).getTime() : 0;
                        const done = end && end < now;
                        return (
                          <div className={`text-xs mt-0.5 ${done ? "text-muted-foreground" : "text-amber-600 dark:text-amber-400"}`} data-testid={done ? "appt-status-done" : "appt-status-planned"}>
                            {done ? "Abgeschlossen" : "Geplant"}
                          </div>
                        );
                      })()}
                    </div>
                </Link>
              ))}
            </div>
          </div>

          {allPhotos.length > 0 && (
            <div className="premium-card overflow-hidden mt-4">
              <div className="p-4 border-b border-border/60 font-display font-semibold">Fotos</div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 p-4">
                {allPhotos.filter(p => p.content_type?.startsWith("image/")).map((p) => (
                  <a key={p.id} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer">
                    <img src={fileUrl(p.storage_path)} alt="" className="w-full h-24 object-cover rounded-md border" />
                  </a>
                ))}
              </div>
            </div>
          )}
      </section>

      <section className="stellar-customer-section">
          <CustomerSectionHeading number="4" icon={History} title="Historie" />
          {history && (history.jobs?.length > 0 || history.invoices?.length > 0 || history.payments?.length > 0) && (
            <div className="premium-card overflow-hidden" data-testid="customer-full-history">
              <div className="grid md:grid-cols-3 gap-0 divide-x divide-border/60">
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <Wrench className="h-3 w-3" /> Aufträge ({history.jobs.length})
                  </div>
                  {history.jobs.slice(0, 5).map((j) => (
                    <Link key={j.id} to={`/auftraege/${j.id}`} className="block py-1 hover:text-primary text-sm truncate">
                      {j.title || j.customer_name} <span className="text-xs text-muted-foreground">· {fmtEur(j.total_price)}</span>
                    </Link>
                  ))}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <FileText className="h-3 w-3" /> Rechnungen ({history.invoices.length})
                  </div>
                  {history.invoices.slice(0, 5).map((i) => (
                    <Link key={i.id} to={`/rechnungen/${i.id}`} className="block py-1 hover:text-primary text-sm font-mono">
                      {i.invoice_number} <span className="text-xs text-muted-foreground font-sans">· {fmtEur(i.total)}</span>
                    </Link>
                  ))}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <Banknote className="h-3 w-3" /> Zahlungen ({history.payments.length})
                  </div>
                  {history.payments.slice(0, 5).map((p) => (
                    <div key={p.id} className="py-1 text-sm truncate">
                      {fmtEur(p.amount)} <span className="text-xs text-muted-foreground">· {new Date(p.payment_date).toLocaleDateString("de-DE")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {(!history || !(history.jobs?.length > 0 || history.invoices?.length > 0 || history.payments?.length > 0)) && (
            <div className="premium-card p-6 text-sm text-muted-foreground">Noch keine Historie.</div>
          )}
      </section>

      <section className="stellar-customer-section">
        <CustomerSectionHeading number="5" icon={Wrench} title="Aktionen" />
        <div className="premium-card stellar-customer-actions flex flex-wrap gap-3 p-4">
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateMut.mutate()} data-testid="save-customer-detail">Speichern</Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNewAppointment} data-testid="customer-new-appointment">
            <CalendarPlus className="h-4 w-4 mr-1" /> Neuer Termin
          </Button>
          <Button variant="ghost" className="text-destructive ml-auto" onClick={() => { if (confirm("Kunde löschen?")) deleteMut.mutate(); }} data-testid="delete-customer">
            <Trash2 className="h-4 w-4 mr-1" /> Kunde löschen
          </Button>
        </div>
      </section>
    </div>
  );
}
